import React, { useEffect, useState } from "react";
import { api, rupiah, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Package } from "lucide-react";

const empty = { name: "", sku: "", category_id: null, price: 0, cost: 0, stock: 0, unit: "pcs", image: "", low_stock_threshold: 5 };

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [del, setDel] = useState(null);

  const load = () => {
    api.get("/products", { params: { search } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [search]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p, image: p.image || "" }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    const payload = { ...form, price: Number(form.price), cost: Number(form.cost), stock: Number(form.stock), low_stock_threshold: Number(form.low_stock_threshold), category_id: form.category_id || null };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Produk diperbarui" : "Produk ditambahkan");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/products/${del.id}`); toast.success("Produk dihapus"); setDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="products-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Manajemen Produk</h1>
          <p className="text-muted-foreground text-sm">Kelola semua produk toko Anda.</p>
        </div>
        <Button onClick={openNew} className="rounded-lg" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1" /> Tambah Produk</Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama / SKU..." className="pl-9 rounded-lg" data-testid="product-search" />
      </div>

      <Card className="border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto kasir-scroll">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-muted-foreground">
              <tr className="text-left">
                <th className="py-3 px-4 font-medium">Produk</th>
                <th className="py-3 px-4 font-medium">Kategori</th>
                <th className="py-3 px-4 font-medium text-right">Harga</th>
                <th className="py-3 px-4 font-medium text-right">Modal</th>
                <th className="py-3 px-4 font-medium text-right">Stok</th>
                <th className="py-3 px-4 font-medium text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`product-row-${p.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-accent/50 flex items-center justify-center overflow-hidden shrink-0">
                        {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-primary/40" />}
                      </div>
                      <div>
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-muted-foreground">{p.sku || "-"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">{p.category_name || <span className="text-muted-foreground">-</span>}</td>
                  <td className="py-3 px-4 text-right font-semibold">{rupiah(p.price)}</td>
                  <td className="py-3 px-4 text-right text-muted-foreground">{rupiah(p.cost)}</td>
                  <td className="py-3 px-4 text-right">
                    <span className={p.stock <= p.low_stock_threshold ? "text-amber-600 font-semibold" : ""}>{p.stock}</span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-primary p-1" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => setDel(p)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {products.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-muted-foreground">Belum ada produk. Tambahkan produk pertama Anda.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Produk" : "Tambah Produk"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nama Produk</Label><Input value={form.name} onChange={set("name")} className="mt-1 rounded-lg" data-testid="product-name-input" /></div>
            <div><Label>SKU</Label><Input value={form.sku} onChange={set("sku")} className="mt-1 rounded-lg" data-testid="product-sku-input" /></div>
            <div>
              <Label>Kategori</Label>
              <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? null : v })}>
                <SelectTrigger className="mt-1 rounded-lg" data-testid="product-category-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Tanpa kategori</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Harga Jual</Label><Input type="number" value={form.price} onChange={set("price")} className="mt-1 rounded-lg" data-testid="product-price-input" /></div>
            <div><Label>Harga Modal</Label><Input type="number" value={form.cost} onChange={set("cost")} className="mt-1 rounded-lg" data-testid="product-cost-input" /></div>
            <div><Label>Stok</Label><Input type="number" value={form.stock} onChange={set("stock")} className="mt-1 rounded-lg" data-testid="product-stock-input" /></div>
            <div><Label>Batas Stok Rendah</Label><Input type="number" value={form.low_stock_threshold} onChange={set("low_stock_threshold")} className="mt-1 rounded-lg" /></div>
            <div className="col-span-2"><Label>URL Gambar (opsional)</Label><Input value={form.image} onChange={set("image")} className="mt-1 rounded-lg" placeholder="https://..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Batal</Button>
            <Button onClick={save} className="rounded-lg" data-testid="save-product-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={() => setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus produk?</AlertDialogTitle>
            <AlertDialogDescription>Produk "{del?.name}" akan dihapus permanen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-lg bg-red-600 hover:bg-red-700" data-testid="confirm-delete-product">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
